import { DateTime } from "luxon";
import { logger } from "@/modules/logger";
import {
  MediaRecorderMimeType,
  type MediaRecorderAudioChunk,
  type MediaRecorderCapabilities,
  type MediaRecorderEngine,
  type MediaRecorderEventHandlers,
} from "@/store/audio/recording/media-types";

const DEFAULT_CHUNK_INTERVAL_MS = 1000;

/**
 * Statistics tracked during recording for debugging and diagnostics.
 */
type ChunkStats = {
  chunksReceived: number;
  chunksStored: number;
  chunksEmpty: number;
  totalBytes: number;
  startedAt: DateTime | null;
  lastChunkAt: DateTime | null;
  lastTrackState: string | null;
};
const DEFAULT_MIME_TYPE = MediaRecorderMimeType.WebmOpus;
const SUPPORTED_MIME_TYPES: MediaRecorderMimeType[] = Object.values(MediaRecorderMimeType);

export type WebMediaRecorderOptions = {
  stream: MediaStream;
  chunkIntervalMs?: number;
  mimeType?: MediaRecorderMimeType;
};

export class WebMediaRecorder implements MediaRecorderEngine {
  private readonly stream: MediaStream;
  private readonly chunkIntervalMs: number;
  private readonly requestedMimeType?: MediaRecorderMimeType;

  private mediaRecorder: MediaRecorder | null = null;
  private lastTimecode: number | null = null;
  private stopPromise: Promise<void> | null = null;
  private resolveStopPromise: (() => void) | null = null;

  private handlers: MediaRecorderEventHandlers = {};

  private handleDataAvailableBound: ((event: BlobEvent) => void) | null = null;
  private handleErrorBound: ((event: Event) => void) | null = null;
  private handleStopBound: (() => void) | null = null;

  private readonly capabilitiesInternal: MediaRecorderCapabilities = {
    audio: true,
    transcripts: false,
    waveform: false,
    pauseResume: true,
  };

  private chunkStats: ChunkStats = {
    chunksReceived: 0,
    chunksStored: 0,
    chunksEmpty: 0,
    totalBytes: 0,
    startedAt: null,
    lastChunkAt: null,
    lastTrackState: null,
  };

  constructor({
    stream,
    chunkIntervalMs = DEFAULT_CHUNK_INTERVAL_MS,
    mimeType,
  }: WebMediaRecorderOptions) {
    this.stream = stream;
    this.chunkIntervalMs = chunkIntervalMs;
    this.requestedMimeType = mimeType;
  }

  get capabilities(): MediaRecorderCapabilities {
    return this.capabilitiesInternal;
  }

  setEventHandlers(handlers: MediaRecorderEventHandlers): void {
    this.handlers = handlers ?? {};
  }

  async start(): Promise<void> {
    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      throw new Error("MediaRecorder API is not available in this environment.");
    }

    const mimeType = this.resolveMimeType();
    const recorder = new MediaRecorder(this.stream, { mimeType });

    this.handleDataAvailableBound = this.handleDataAvailable.bind(this);
    this.handleErrorBound = this.handleError.bind(this);
    this.handleStopBound = this.handleStop.bind(this);

    recorder.addEventListener("dataavailable", this.handleDataAvailableBound);
    recorder.addEventListener("error", this.handleErrorBound);
    recorder.addEventListener("stop", this.handleStopBound);

    this.mediaRecorder = recorder;
    this.lastTimecode = null;
    this.stopPromise = null;
    this.resolveStopPromise = null;

    // Reset chunk stats for new recording
    this.chunkStats = {
      chunksReceived: 0,
      chunksStored: 0,
      chunksEmpty: 0,
      totalBytes: 0,
      startedAt: DateTime.now(),
      lastChunkAt: null,
      lastTrackState: null,
    };

    recorder.start(this.chunkIntervalMs);

    logger.info({
      message: "[WebMediaRecorder] Recording started",
      info: {
        mimeType,
        chunkIntervalMs: this.chunkIntervalMs,
        trackCount: this.stream.getTracks().length,
        trackStates: this.getTrackStates(),
      },
    });

    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    if (!this.mediaRecorder) return;

    if (this.stopPromise) {
      await this.stopPromise;
      return;
    }

    // Log recording summary before stopping
    this.logRecordingSummary();

    this.stopPromise = new Promise<void>(resolve => {
      this.resolveStopPromise = resolve;
    });

    try {
      this.mediaRecorder.stop();
      await this.stopPromise;
    } catch (error) {
      this.stopPromise = null;
      this.resolveStopPromise = null;
      throw error;
    }
  }

  /**
   * Logs a summary of the recording session for debugging.
   * Wrapped in try/catch to ensure logging failures don't break recording.
   */
  private logRecordingSummary(): void {
    try {
      const duration = this.chunkStats.startedAt
        ? DateTime.now().diff(this.chunkStats.startedAt)
        : null;

      const summary = {
        durationSec: duration ? Math.round(duration.as("seconds")) : null,
        durationFormatted: duration?.toFormat("h:mm:ss") ?? null,
        chunksReceived: this.chunkStats.chunksReceived,
        chunksStored: this.chunkStats.chunksStored,
        chunksEmpty: this.chunkStats.chunksEmpty,
        totalBytes: this.chunkStats.totalBytes,
        lastTrackState: this.chunkStats.lastTrackState,
        currentTrackState: this.getTrackStates(),
        mimeType: this.resolveMimeType(),
      };

      // Use warn level if we have potential issues
      const hasIssues =
        this.chunkStats.chunksStored === 0 ||
        this.chunkStats.chunksEmpty > 0 ||
        this.chunkStats.totalBytes === 0;

      if (hasIssues) {
        logger.warn({
          message: "[WebMediaRecorder] Recording completed with potential issues",
          info: summary,
        });
      } else {
        logger.info({
          message: "[WebMediaRecorder] Recording completed",
          info: summary,
        });
      }
    } catch {
      // Don't let logging failures break recording
    }
  }

  async pause(): Promise<void> {
    if (!this.mediaRecorder) return;
    this.mediaRecorder.pause();
  }

  async resume(): Promise<void> {
    if (!this.mediaRecorder) return;
    this.mediaRecorder.resume();
  }

  dispose(): void {
    void this.stop();
    this.cleanupRecorder();
    this.stream.getTracks().forEach(track => track.stop());
  }

  private handleDataAvailable(event: BlobEvent) {
    this.chunkStats.chunksReceived++;
    this.chunkStats.lastChunkAt = DateTime.now();
    this.chunkStats.lastTrackState = this.getTrackStates();

    if (!event.data || event.data.size === 0) {
      this.chunkStats.chunksEmpty++;
      return;
    }

    const previousTimecode = this.lastTimecode;
    const durationMs =
      typeof event.timecode === "number" && previousTimecode !== null
        ? Math.max(event.timecode - previousTimecode, this.chunkIntervalMs)
        : this.chunkIntervalMs;

    this.lastTimecode =
      typeof event.timecode === "number"
        ? event.timecode
        : previousTimecode !== null
          ? previousTimecode + durationMs
          : durationMs;

    const chunk: MediaRecorderAudioChunk = {
      blob: event.data,
      durationMs,
      mimeType: resolveMimeType({
        reportedType: event.data.type,
        fallback: this.resolveMimeType(),
      }),
      byteLength: event.data.size,
    };

    this.chunkStats.chunksStored++;
    this.chunkStats.totalBytes += event.data.size;

    try {
      this.handlers.onAudioChunk?.({ chunk });
    } catch (error) {
      this.handlers.onError?.({ error });
    }
  }

  private handleError(event: Event) {
    const domException = (event as { error?: DOMException }).error;
    if (domException) {
      this.handlers.onError?.({ error: domException });
      return;
    }

    // Build a descriptive error when browser doesn't provide a DOMException
    const recorderState = this.mediaRecorder?.state ?? "unknown";
    const trackStates = this.stream
      .getTracks()
      .map(t => `${t.kind}:${t.readyState}`)
      .join(",");

    const error = new Error(
      `MediaRecorder failed (state: ${recorderState}, tracks: ${trackStates})`
    );
    this.handlers.onError?.({ error });
  }

  private handleStop() {
    try {
      this.handlers.onStop?.();
    } finally {
      this.cleanupRecorder();
      this.resolveStopPromise?.();
      this.stopPromise = null;
      this.resolveStopPromise = null;
    }
  }

  private cleanupRecorder() {
    if (!this.mediaRecorder) return;

    if (this.handleDataAvailableBound) {
      this.mediaRecorder.removeEventListener("dataavailable", this.handleDataAvailableBound);
    }
    if (this.handleErrorBound) {
      this.mediaRecorder.removeEventListener("error", this.handleErrorBound);
    }
    if (this.handleStopBound) {
      this.mediaRecorder.removeEventListener("stop", this.handleStopBound);
    }

    this.mediaRecorder = null;
    this.handleDataAvailableBound = null;
    this.handleErrorBound = null;
    this.handleStopBound = null;
    this.lastTimecode = null;
  }

  private resolveMimeType(): MediaRecorderMimeType {
    if (this.requestedMimeType) {
      return this.requestedMimeType;
    }

    if (typeof MediaRecorder.isTypeSupported === "function") {
      for (const candidate of SUPPORTED_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(candidate)) {
          return candidate;
        }
      }
    }

    return DEFAULT_MIME_TYPE;
  }

  /**
   * Returns a string describing the current state of all tracks in the stream.
   * Format: "audio:live" or "audio:ended" etc.
   */
  private getTrackStates(): string {
    try {
      return this.stream
        .getTracks()
        .map(t => `${t.kind}:${t.readyState}`)
        .join(",");
    } catch {
      return "unknown";
    }
  }
}

function resolveMimeType({
  reportedType,
  fallback,
}: {
  reportedType: string;
  fallback: MediaRecorderMimeType;
}): MediaRecorderMimeType {
  if ((SUPPORTED_MIME_TYPES as string[]).includes(reportedType)) {
    return reportedType as MediaRecorderMimeType;
  }
  return fallback;
}
